// Supervision: due board, rules, settings, short form.
// Cadence and scheduling rules live in the database (supervision_rules,
// supervision_status, supervision_schedule_*, supervisory_visit_submit).
import { supabase } from './supabase';
import { getAxisCareVisitsByDateRange } from './axiscare';

export const KIND_LABEL = { aide: 'Caregiver (PSS / CNA / family)', lpn: 'LPN', rn: 'RN', client: 'Client review (RN)' };
// Subjects a supervisory visit can be about, in the order the form offers them.
export const SUBJECT_KINDS = ['aide', 'lpn', 'rn'];
export const REASONS = [['routine', 'Routine'], ['initial', 'Initial (within first month)'], ['follow_up', 'Follow-up'], ['sentinel', 'Sentinel event'], ['unannounced', 'Unannounced']];

export const getSupervisionBoard = () =>
  supabase.from('supervision_status').select('*').order('next_due').order('client_last');

export const getSupervisionRules = () => supabase.from('supervision_rules').select('*').order('kind');
export const saveSupervisionRule = (r) => supabase.from('supervision_rules').update(r).eq('id', r.id);
export const getSupervisionSettings = () => supabase.from('supervision_settings').select('*, supervisor:staff ( id, full_name )').limit(1).maybeSingle();
export const saveSupervisionSettings = (agencyId, patch) => supabase.from('supervision_settings').update({ ...patch, updated_at: new Date().toISOString() }).eq('agency_id', agencyId);

export const scheduleSupervision = async (kind, clientId, staffId, on = null, supervisorStaffId = null) => {
  const { data, error } = await supabase.rpc('supervision_schedule_pair', { p_kind: kind, p_client_id: clientId, p_staff_id: staffId, p_on: on, p_supervisor_staff_id: supervisorStaffId });
  if (error) throw new Error(error.message);
  return data;
};
export const scheduleAllDue = async (horizonDays = 14) => {
  const { data, error } = await supabase.rpc('supervision_schedule_due', { p_horizon_days: horizonDays });
  if (error) throw new Error(error.message);
  return data;
};
// A visit is addressed either by its calendar visit (scheduled ones) or by
// the supervisor_visits row itself (backfilled history has no calendar visit).
export const getSupervisionContext = async ({ visitId = null, supervisorVisitId = null }) => {
  const { data, error } = await supabase.rpc('supervision_context', { p_visit_id: visitId, p_supervisor_visit_id: supervisorVisitId });
  if (error) throw new Error(error.message);
  return data;
};
export const submitSupervisoryVisit = async ({ visitId = null, supervisorVisitId = null }, payload) => {
  const { data, error } = await supabase.rpc('supervisory_visit_submit', { p_visit_id: visitId, p: payload, p_supervisor_visit_id: supervisorVisitId });
  if (error) throw new Error(error.message);
  return data;
};
// Files the signed visit to the client's Drive folder on the agency template
// and returns the link. Safe to call again — an already-filed report is returned.
export const fileSupervisionReport = async (supervisorVisitId, { force = false } = {}) => {
  const { data, error } = await supabase.functions.invoke('supervision-report', { body: { supervisor_visit_id: supervisorVisitId, force } });
  if (error) {
    let msg = error.message;
    try { const body = await error.context?.json?.(); if (body?.error) msg = body.error; } catch { /* keep the generic message */ }
    throw new Error(msg);
  }
  if (!data?.success) throw new Error(data?.error || 'Could not file the report');
  return data.webViewLink;
};
// One row per interval from `from` to today, for the nurse to document.
export const backfillSupervision = async (kind, clientId, staffId, from, familyName = null) => {
  const { data, error } = await supabase.rpc('supervision_backfill', { p_kind: kind, p_client_id: clientId, p_staff_id: staffId, p_from: from, p_supervised_family_name: familyName });
  if (error) throw new Error(error.message);
  return data;
};
export const cancelScheduledSupervision = async (supervisorVisitId, visitId) => {
  const { error: e1 } = await supabase.from('supervisor_visits').update({ status: 'archived' }).eq('id', supervisorVisitId).eq('status', 'scheduled');
  if (e1) throw new Error(e1.message);
  if (visitId) {
    const { error: e2 } = await supabase.from('visits').update({ status: 'cancelled', evv_status: 'UNSR', modification_reason: 'Supervisory visit cancelled' }).eq('id', visitId).eq('status', 'scheduled');
    if (e2) throw new Error(e2.message);
  }
};
export const getStaffSupervisionHistory = (staffId) =>
  supabase.from('supervisor_visits')
    .select('id, visit_date, subject_kind, status, supervising_credential, supervisor_name, competent, care_per_plan, concerns, rn_cosign_due, rn_countersigned_at, client_name, client_id, supervised_staff_id, supervising_staff_id')
    .or(`supervised_staff_id.eq.${staffId},supervising_staff_id.eq.${staffId}`)
    .neq('status', 'archived')
    .order('visit_date', { ascending: false });
export const getNurseStaff = () => supabase.from('staff').select('id, full_name, position_code').in('position_code', ['RN', 'LPN']).eq('status', 'active').order('full_name');

// Plans a history backfill from AxisCare: for each client passed (needs
// axiscare_client_id), every caregiver / LPN who has visited them and the
// date of that person's first visit — the start of service for supervision
// purposes. RNs are left out: RN supervision has no cadence. One AxisCare
// fetch covers every client, so this is fine to run for the whole roster.
export const BACKFILL_SINCE = '2026-01-01';
export const planSupervisionBackfill = async (clients) => {
  const today = new Date().toISOString().slice(0, 10);
  const { data, error } = await getAxisCareVisitsByDateRange(BACKFILL_SINCE, today);
  if (error) throw new Error(error.message || 'AxisCare fetch failed');
  const raw = data?.results?.visits; const arr = Array.isArray(raw) ? raw : Object.values(raw || {});
  const byAxis = {}; clients.forEach(c => { if (c.axiscare_client_id) byAxis[String(c.axiscare_client_id)] = c; });
  const first = {}; // `${clientUuid}|${caregiverAxisId}` → first date
  for (const v of arr) {
    const c = byAxis[String(v.client?.id)];
    if (!c || v.removed || !v.caregiver?.id) continue;
    const d = (v.scheduledStartDate || v.startDate || '').slice(0, 10);
    const k = `${c.id}|${v.caregiver.id}`;
    if (d && (!first[k] || d < first[k])) first[k] = d;
  }
  const axisIds = [...new Set(Object.keys(first).map(k => Number(k.split('|')[1])).filter(Boolean))];
  const { data: staff } = axisIds.length ? await supabase.from('staff').select('id, axiscare_id, full_name, position_code').in('axiscare_id', axisIds) : { data: [] };
  const staffByAxis = {}; (staff || []).forEach(s => { staffByAxis[String(s.axiscare_id)] = s; });
  const pairs = [];
  for (const [k, from] of Object.entries(first)) {
    const [clientId, cg] = k.split('|'); const s = staffByAxis[cg]; const c = clients.find(x => x.id === clientId);
    if (!s || !c || s.position_code === 'RN') continue;
    const kind = s.position_code === 'LPN' ? 'lpn' : 'aide';
    const interval = kind === 'lpn' ? 30 : 14;
    const slots = Math.max(0, Math.floor((new Date(today) - new Date(from)) / 86400000 / interval));
    pairs.push({ clientId, clientRef: c.client_id, clientName: `${c.last_name}, ${c.first_name}`, kind, staffId: s.id, name: s.full_name, position: s.position_code, from, slots });
  }
  return pairs.sort((a, b) => a.clientName.localeCompare(b.clientName) || a.name.localeCompare(b.name));
};
export const runSupervisionBackfill = async (pairs) => {
  let n = 0;
  for (const p of pairs) n += await backfillSupervision(p.kind, p.clientId, p.staffId, p.from);
  return n;
};
